import React, { useMemo } from 'react';
import {
  AnswerInput,
  HelperText,
  ProgramEnumResponse,
  useProgram,
} from 'shared/providers/ProgramProvider';
import { SelectorOptions } from 'shared/design-system/components/Selector';
import {
  directRegex,
  indirectRegex,
  renderDirectBeneficiaryText,
  renderIndirectBeneficiaryText,
} from 'npo/pages/ReportBuilder/Components/SectionMultiRadio';
import { beneficiaryTermQuestionId } from 'shared/providers/IndicatorProvider/utils';
import SelectorField from './SelectorField';

interface ServiceTypeFieldProps {
  sectionKey: string | null | undefined;
  questionId: number;
  enumOptions: ProgramEnumResponse[];
  value: AnswerInput;
  onChange: (next: AnswerInput) => void;
  disabled?: boolean;
}

// Q40006 "How does your program provide its services?" — mirrors the
// ReportBuilder SectionMultiRadio rendering: the `<Primary_beneficiary>`
// placeholder in each option is replaced with the Q40004 term, options are
// labelled Directly / Indirectly, and the *resolved* option text is what
// gets persisted (downstream helpers regex-match the stored text).
const ServiceTypeField: React.FC<ServiceTypeFieldProps> = ({
  sectionKey,
  questionId,
  enumOptions,
  value,
  onChange,
  disabled,
}) => {
  const { answers } = useProgram();
  const beneficiaryTerm =
    answers[beneficiaryTermQuestionId]?.text || 'Beneficiaries';

  const options = useMemo(
    () =>
      enumOptions.map(opt => ({
        ...opt,
        label: opt.response
          .replace(/<Primary_beneficiary>/g, beneficiaryTerm)
          .replace(/<Primary_beneficiary_or_Intermediary>/g, beneficiaryTerm),
      })),
    [enumOptions, beneficiaryTerm],
  );

  const current = value.text ?? value.questionText ?? '';
  const isSelected = (opt: { dbId: number; label: string }) =>
    value.answerId === opt.dbId ||
    current === opt.label ||
    (directRegex.test(current) && directRegex.test(opt.label)) ||
    (indirectRegex.test(current) && indirectRegex.test(opt.label));

  return (
    <>
      <HelperText sectionKey={sectionKey} questionId={questionId} />
      <SelectorField>
        <SelectorOptions
          options={options}
          variant="radio"
          margin="0"
          maxWidth="100%"
          disabled={disabled}
          getLabel={opt =>
            directRegex.test(opt.label)
              ? renderDirectBeneficiaryText(opt.label)
              : indirectRegex.test(opt.label)
              ? renderIndirectBeneficiaryText(opt.label)
              : opt.label
          }
          getSelected={isSelected}
          onClick={opt => {
            if (disabled) return;
            onChange({
              ...value,
              answerId: opt.dbId,
              text: opt.label,
              questionText: opt.label,
            });
          }}
        />
      </SelectorField>
    </>
  );
};

export default ServiceTypeField;
